import type { QuoteSnapshot } from '../types.ts'
import type { TickerWithValue } from '../hooks/useCategoriesTree.ts'
import styles from './TickersRows.module.css'

type Props = {
  tickers: TickerWithValue[]
  quotesBySymbol: Record<string, QuoteSnapshot>
  deletingId?: number | null
  onEdit: (ticker: TickerWithValue) => void
  onDelete: (ticker: TickerWithValue) => void
}

function formatDecimal(value?: string | null) {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return value

  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  })
}

function formatFlexibleDecimal(value?: string | null) {
  if (value === null || value === undefined || value === '') return '—'
  const num = Number(value)
  if (Number.isNaN(num)) return value

  return num.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 20,
  })
}

function formatCurrency(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatPercent(value?: number | null) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—'

  const absValue = Math.abs(value).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  const prefix = value > 0 ? '+' : value < 0 ? '-' : ''

  return `${prefix}${absValue}%`
}

function TickerRow({
  ticker,
  quote,
  deletingId,
  onEdit,
  onDelete,
}: {
  ticker: TickerWithValue
  quote?: QuoteSnapshot
  deletingId?: number | null
  onEdit: (ticker: TickerWithValue) => void
  onDelete: (ticker: TickerWithValue) => void
}) {
  const change = quote?.regularMarketChangePercent
  const changeClass =
    change === null || change === undefined
      ? styles.quoteNeutral
      : change > 0
        ? styles.quoteUp
        : change < 0
          ? styles.quoteDown
          : styles.quoteNeutral

  return (
    <div className={styles.tickerRow}>
      <div className={styles.tickerName}>{ticker.ticker}</div>
      <div>{formatFlexibleDecimal(ticker.qtd)}</div>
      <div>{formatDecimal(ticker.preco_medio)}</div>
      <div>{formatCurrency(quote?.regularMarketPrice)}</div>
      <div className={changeClass}>{formatPercent(change)}</div>
      {/* Novo campo calculado vindo pronto da árvore derivada. */}
      <div>{formatCurrency(ticker.value)}</div>
      {/*<div>{ticker.cnpj ?? '—'}</div>*/}
      <div className={styles.actions}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => onEdit(ticker)}
          title="Editar"
        >
          ✎
        </button>
        <button
          type="button"
          className="btn btn--danger btn--sm"
          onClick={() => onDelete(ticker)}
          disabled={deletingId === ticker.id}
          title="Deletar"
        >
          {deletingId === ticker.id ? '…' : '✕'}
        </button>
      </div>
    </div>
  )
}

export function TickersRows({ tickers, quotesBySymbol, deletingId, onEdit, onDelete }: Props) {
  return (
    <div className={styles.rows}>
      <div className={styles.header}>
        <div></div>
        <div>Qtd</div>
        <div>PM</div>
        <div>Preço</div>
        <div>Var %</div>
        <div>Valor</div>
        {/*<div>CNPJ</div>*/}
        <div className={styles.actionsHeader}></div>
      </div>

      {tickers.map((ticker) => (
        <TickerRow
          key={ticker.id}
          ticker={ticker}
          quote={quotesBySymbol[ticker.ticker]}
          deletingId={deletingId}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}
    </div>
  )
}
